import { Redirect, Stack, useLocalSearchParams, usePathname, useRouter } from 'expo-router'
import { AlertCircle, RefreshCw, Shield, WifiOff } from 'lucide-react-native'
import { useEffect, useRef } from 'react'
import { Linking, Platform, useWindowDimensions, View } from 'react-native'
import { useTheme } from 'react-native-paper'
import { useConnectivityMonitor } from '@/hooks/useConnectivityMonitor'
import { useDeviceTrackingSettings } from '@/hooks/useDeviceTrackingSettings'
import { useLocationAuthLogger } from '@/hooks/useLocationAuthLogger'
import { useLocationTracking } from '@/hooks/useLocationTracking'
import { useOrgBySlug } from '@/hooks/useOrgBySlug'
import { usePushTokenRegistration } from '@/hooks/usePushTokenRegistration'
import { useTrackingWatchdog } from '@/hooks/useTrackingWatchdog'
import { useWebAppUpdates } from '@/hooks/useWebAppUpdates'
import { useAuth } from '@/lib/auth'
import { signInUrl } from '@/lib/auth-redirect'
import { BREAKPOINTS } from '@/lib/config'
import { useImpersonation } from '@/lib/impersonation'
import { NewlyCreatedRecordProvider } from '@/lib/newlyCreatedRecord'
import { useNoticeWhile } from '@/lib/notice'
import { preloadStores } from '@/lib/pocketbase'
import { getStackScreenOptions, type RouteName, type RouteOptions, routes } from '@/lib/routes'
import { AccessDeniedScreen } from '@/ui/AccessDeniedScreen'
import { Button } from '@/ui/Button'
import { DesktopShell } from '@/ui/DesktopShell'
import { LoadingIndicator } from '@/ui/LoadingIndicator'
import { ParentLinkButton } from '@/ui/ParentLinkButton'

function createParentLinkButton(orgSlug: string) {
    return (fallbackKey: RouteName) => {
        return () => <ParentLinkButton orgSlug={orgSlug} fallbackKey={fallbackKey} />
    }
}

export default function OrgSlugLayout() {
    const { orgSlug } = useLocalSearchParams<{ orgSlug: string }>()
    const pathname = usePathname()
    const theme = useTheme()
    const router = useRouter()
    const { width } = useWindowDimensions()
    const { isLoggedIn, isInitializing, user } = useAuth({ throwIfAnon: false })
    const { org, role, hasAccess, isLoading: isRoleLoading } = useOrgBySlug(orgSlug)
    const { pendingRedirect, clearPendingRedirect, impersonatedUser, stopImpersonation } =
        useImpersonation()

    useEffect(() => {
        if (pendingRedirect) {
            clearPendingRedirect()
            router.replace(pendingRedirect as '/')
        }
    }, [pendingRedirect, clearPendingRedirect, router])

    const isDesktop = width >= BREAKPOINTS.lg
    const showDesktopLayout = isDesktop && role !== 'workforce' && !isRoleLoading

    const { isConnected } = useConnectivityMonitor()
    const hasRedirectedOffline = useRef(false)

    useNoticeWhile(isConnected === false, {
        id: 'offline',
        severity: 'error',
        icon: WifiOff,
        message: 'You are offline — data will sync when reconnected',
        dismissible: false,
    })

    useEffect(() => {
        if (isConnected === false && !hasRedirectedOffline.current) {
            hasRedirectedOffline.current = true
            router.navigate(`/${orgSlug}/entry`)
        }
        if (isConnected) {
            hasRedirectedOffline.current = false
        }
    }, [isConnected, orgSlug, router])

    useLocationTracking()
    useLocationAuthLogger(org?.id ?? null)
    useTrackingWatchdog(org?.id ?? null)
    usePushTokenRegistration()
    const { updateAvailable } = useWebAppUpdates()

    useNoticeWhile(updateAvailable, {
        id: 'app-update',
        severity: 'info',
        icon: RefreshCw,
        message: 'New version available',
        actions: [
            {
                label: 'Reload',
                onPress: () => {
                    if (Platform.OS === 'web') window.location.reload()
                },
                dismissOnPress: false,
            },
        ],
    })

    useNoticeWhile(!!impersonatedUser, {
        id: 'impersonation',
        severity: 'neutral',
        icon: Shield,
        title: impersonatedUser ? `Viewing as: ${impersonatedUser.name}` : '',
        message: impersonatedUser?.email ?? '',
        actions: [{ label: 'Exit', onPress: stopImpersonation, dismissOnPress: false }],
        dismissible: false,
    })

    const { showPermissionWarning } = useDeviceTrackingSettings()
    const accountTrackingEnabled = !user?.geo_tracking_disabled
    const locationPermissionVisible =
        Platform.OS !== 'web' && accountTrackingEnabled && showPermissionWarning

    const openLocationSettings = () => {
        if (Platform.OS === 'ios') {
            Linking.openURL('app-settings:')
        } else {
            Linking.openSettings()
        }
    }

    useNoticeWhile(locationPermissionVisible, {
        id: 'location-permission',
        severity: 'error',
        icon: AlertCircle,
        title: 'Location Permission Required',
        message:
            'Enable "Always" to track location, or disable tracking for this device in My Details.',
        actions: [
            { label: 'Settings', onPress: openLocationSettings, dismissOnPress: false },
            {
                label: 'My Details',
                onPress: () => router.push(`/${orgSlug}/settings/my-details?scrollTo=location`),
                dismissOnPress: false,
            },
        ],
        dismissible: false,
    })

    useEffect(() => {
        if (isLoggedIn) {
            preloadStores()
        }
    }, [isLoggedIn])

    if (isInitializing) {
        return <LoadingIndicator />
    }

    if (!isLoggedIn) {
        return <Redirect href={signInUrl(pathname) as '/'} />
    }

    if (isRoleLoading && isConnected !== false) {
        return <LoadingIndicator />
    }

    if (!isRoleLoading && !hasAccess && isConnected !== false) {
        return <AccessDeniedScreen />
    }

    const screenOptions = getStackScreenOptions(theme)
    const getParentLinkButton = createParentLinkButton(orgSlug)

    const stackContent = (
        <Stack screenOptions={screenOptions}>
            {Object.entries(routes).map(([name, routeConfig]) => {
                const { headerRightButton, parentRoute, ...config } = routeConfig as RouteOptions

                const fallbackKey = (parentRoute || 'index') as RouteName
                const fallback = routes[fallbackKey] as RouteOptions

                const hideBackLink = showDesktopLayout && fallbackKey === 'index'

                const options = {
                    ...config,
                    ...(Platform.OS === 'ios'
                        ? {
                              headerBackTitle: fallback?.title,
                          }
                        : hideBackLink
                          ? { headerLeft: () => null }
                          : {
                                headerLeft: getParentLinkButton(fallbackKey),
                            }),

                    ...(headerRightButton && {
                        headerRight: () => (
                            <Button
                                onPress={() =>
                                    router.push(`/${orgSlug}/${headerRightButton.route}`)
                                }
                                icon={headerRightButton.icon}
                                title={headerRightButton.label}
                                mode="text"
                                compact
                            />
                        ),
                    }),
                }

                return <Stack.Screen key={name} name={name} options={options} />
            })}
        </Stack>
    )

    return (
        <NewlyCreatedRecordProvider>
            <View style={{ flex: 1 }}>
                {showDesktopLayout ? <DesktopShell>{stackContent}</DesktopShell> : stackContent}
            </View>
        </NewlyCreatedRecordProvider>
    )
}
